import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  WRITE_ROLES,
  type ApplicationProposal,
  type CloudConnection,
  type Criticality,
  type ProposalEvidenceKind,
  type ProposalStatus,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { COMPONENT_KIND_LABEL } from '../../lib/components';
import { layoutRadar } from '../../lib/shadow-radar';
import { useAuth } from '../auth/auth';
import { ShadowRadar } from './ShadowRadar';

const date = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const errorText = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message : fallback;

const EVIDENCE: Record<ProposalEvidenceKind, string> = {
  APPLICATION_TAG: 'Application tag',
  STACK: 'Same CloudFormation stack',
  FRONT_DOOR: 'Routes to',
  HOST: 'Single host',
};

const STATUS: Record<ProposalStatus, { label: string; cls: string }> = {
  NEW: { label: 'No owner', cls: 'status-active' },
  PARTIAL: { label: 'Partly owned', cls: 'status-waiting' },
  COVERED: { label: 'Already an application', cls: 'status-done' },
  DISMISSED: { label: 'Dismissed', cls: 'status-muted' },
};

type Tab = 'decide' | 'dismissed' | 'covered';
const TAB_STATUSES: Record<Tab, ProposalStatus[]> = {
  decide: ['NEW', 'PARTIAL'],
  dismissed: ['DISMISSED'],
  covered: ['COVERED'],
};

/**
 * Applications discovery found in a connected account (ADR 0008).
 *
 * Every proposal is evidence, not a verdict: why its hosts were grouped, what lies behind them and
 * whether an application already claims them. Accepting creates an ordinary application, built
 * by the server from the discovery. It never creates a scope — the first scope of an engagement
 * is only pre-filled with the hosts, and still needs a window, an authorisation and an approver.
 */
export function ProposalsPanel({ connection }: { connection: CloudConnection }) {
  const { user } = useAuth();
  const mayWrite = user ? WRITE_ROLES.has(user.role) : false;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('decide');
  const [selected, setSelected] = useState<string[]>([]);
  const [mergedName, setMergedName] = useState('');

  const proposals = useQuery({
    queryKey: ['proposals', connection.id],
    queryFn: () => api.listProposals(connection.id),
  });
  const runs = useQuery({
    queryKey: ['discoveries', connection.id],
    queryFn: () => api.listDiscoveries(connection.id),
    refetchInterval: (q) =>
      ['QUEUED', 'RUNNING'].includes(q.state.data?.items[0]?.status ?? '') ? 5000 : false,
  });
  const latestRun = runs.data?.items[0];
  const discovering = latestRun?.status === 'QUEUED' || latestRun?.status === 'RUNNING';

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['proposals', connection.id] }),
      queryClient.invalidateQueries({ queryKey: ['cloudConnections'] }),
      queryClient.invalidateQueries({ queryKey: ['applications'] }),
    ]);
  };

  const discover = useMutation({
    mutationFn: () => api.startDiscovery(connection.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['discoveries', connection.id] }),
  });

  const accept = useMutation({
    mutationFn: (input: { keys: string[]; name?: string; criticality: Criticality }) =>
      api.acceptProposals(connection.id, {
        discoveryId: proposals.data!.discovery!.id,
        keys: input.keys,
        criticality: input.criticality,
        ...(input.name ? { name: input.name } : {}),
      }),
    onSuccess: async (created) => {
      setSelected([]);
      await refresh();
      void navigate(`/app/surface?app=${created.id}`);
    },
  });

  const dismiss = useMutation({
    mutationFn: (key: string) => api.dismissProposal(connection.id, key, connection.version),
    onSuccess: refresh,
  });

  // A finished discovery since the list was computed means the list is stale.
  const listed = proposals.data?.discovery;
  const newer =
    latestRun?.status === 'SUCCEEDED' && listed !== undefined && latestRun.id !== listed?.id;

  const items = (proposals.data?.proposals ?? []).filter((p) =>
    TAB_STATUSES[tab].includes(p.status),
  );
  const summary = proposals.data?.summary;
  const all = proposals.data?.proposals ?? [];
  const dots = useMemo(() => layoutRadar(all), [all]);
  const publicHosts = new Set(all.flatMap((p) => p.hosts)).size;
  const [focusKey, setFocusKey] = useState<string | null>(null);
  // The selected dot, else the most exposed proposal nobody owns.
  const focus =
    all.find((p) => p.key === focusKey) ??
    all.find((p) => p.key === dots.find((d) => d.tone !== 'owned')?.key);
  const toggle = (key: string) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  return (
    <section className="proposals" aria-labelledby={`proposals-${connection.id}`}>
      <div className="panel-head">
        <h3 id={`proposals-${connection.id}`} className="detail-title">
          Applications found in this account
        </h3>
        {mayWrite ? (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={discover.isPending || discovering}
            onClick={() => discover.mutate()}
          >
            {discovering ? 'Discovering…' : 'Discover now'}
          </button>
        ) : null}
      </div>

      {proposals.isLoading ? (
        <p className="muted">Reading the latest discovery…</p>
      ) : proposals.isError ? (
        <p className="login-error" role="alert">
          {errorText(proposals.error, 'Could not load the proposals.')}
        </p>
      ) : !listed ? (
        <p className="muted">
          No discovery has finished yet. Run one, and Guardian will propose the applications it
          finds — from DNS names, application tags and stacks — for you to accept or dismiss.
        </p>
      ) : (
        <>
          <p className="muted surface-basis">
            From the discovery of {date(listed.finishedAt ?? listed.requestedAt)}.
            {proposals.data?.integrity === 'changed' ? (
              <strong className="sev-high">
                {' '}
                The stored discovery no longer matches its recorded hash — run a new one before
                accepting anything.
              </strong>
            ) : null}
            {newer ? (
              <>
                {' '}
                A newer discovery has finished.{' '}
                <button type="button" className="link-btn" onClick={() => void refresh()}>
                  Show it
                </button>
              </>
            ) : null}
          </p>

          <div className="sr-grid">
            <section className="sr-card" aria-labelledby={`radar-${connection.id}`}>
              <h4 id={`radar-${connection.id}`} className="xp-eyebrow">
                Shadow surface
              </h4>
              <div className="sr-headline">
                <span className={`sr-big${(summary?.unclaimedHosts ?? 0) > 0 ? ' is-warn' : ''}`}>
                  {summary?.unclaimedHosts ?? 0}
                </span>
                <span className="muted">
                  of {publicHosts} public {publicHosts === 1 ? 'host' : 'hosts'} belong to no
                  application
                </span>
              </div>
              <ShadowRadar dots={dots} selected={focus?.key ?? null} onSelect={setFocusKey} />
              <div className="sr-legend">
                <span>
                  <i className="sr-sw sr-sw-danger" />
                  Secret or public data behind it
                </span>
                <span>
                  <i className="sr-sw sr-sw-warn" />
                  No owner
                </span>
                <span>
                  <i className="sr-sw sr-sw-owned" />
                  Owned
                </span>
              </div>
            </section>
            <section className="sr-focus" aria-label="Selected proposal">
              <h4 className="xp-eyebrow">
                {focus && focus.status !== 'COVERED' ? 'Needs a decision' : 'Selected'}
              </h4>
              {focus ? (
                <ul className="proposal-list">
                  <ProposalCard
                    proposal={focus}
                    mayWrite={mayWrite && proposals.data?.integrity === 'verified'}
                    selected={selected.includes(focus.key)}
                    onToggle={() => toggle(focus.key)}
                    busy={accept.isPending || dismiss.isPending}
                    onAccept={(criticality) => accept.mutate({ keys: [focus.key], criticality })}
                    onDismiss={() => dismiss.mutate(focus.key)}
                  />
                </ul>
              ) : (
                <p className="muted">Every public host found belongs to an application.</p>
              )}
            </section>
          </div>

          <div className="filters" role="tablist" aria-label="Proposals">
            {(
              [
                ['decide', `To decide (${(summary?.new ?? 0) + (summary?.partial ?? 0)})`],
                ['covered', `Already applications (${summary?.covered ?? 0})`],
                ['dismissed', `Dismissed (${summary?.dismissed ?? 0})`],
              ] as const
            ).map(([t, label]) => (
              <button
                key={t}
                type="button"
                role="tab"
                className="filter"
                aria-selected={tab === t}
                aria-pressed={tab === t}
                onClick={() => {
                  setTab(t);
                  setSelected([]);
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {mayWrite && selected.length > 1 ? (
            <div className="merge-bar">
              <span>{selected.length} selected — accept them as one application:</span>
              <input
                className="input"
                aria-label="Name for the combined application"
                placeholder="Name"
                maxLength={120}
                value={mergedName}
                onChange={(e) => setMergedName(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={accept.isPending}
                onClick={() =>
                  accept.mutate({
                    keys: selected,
                    criticality: 'MEDIUM',
                    ...(mergedName.trim() ? { name: mergedName.trim() } : {}),
                  })
                }
              >
                Accept together
              </button>
            </div>
          ) : null}

          {accept.error || dismiss.error || discover.error ? (
            <p className="login-error" role="alert">
              {errorText(accept.error ?? dismiss.error ?? discover.error, 'That did not work.')}
            </p>
          ) : null}

          {items.length === 0 ? (
            <p className="muted">Nothing here.</p>
          ) : (
            <ul className="proposal-list">
              {items.map((p) => (
                <ProposalCard
                  key={p.key}
                  proposal={p}
                  mayWrite={mayWrite && proposals.data?.integrity === 'verified'}
                  selected={selected.includes(p.key)}
                  onToggle={() => toggle(p.key)}
                  busy={accept.isPending || dismiss.isPending}
                  onAccept={(criticality) => accept.mutate({ keys: [p.key], criticality })}
                  onDismiss={() => dismiss.mutate(p.key)}
                />
              ))}
            </ul>
          )}
          {summary?.truncated ? (
            <p className="muted">More were found than are listed; the strongest are shown.</p>
          ) : null}
        </>
      )}
    </section>
  );
}

function ProposalCard({
  proposal: p,
  mayWrite,
  selected,
  onToggle,
  busy,
  onAccept,
  onDismiss,
}: {
  proposal: ApplicationProposal;
  mayWrite: boolean;
  selected: boolean;
  onToggle: () => void;
  busy: boolean;
  onAccept: (criticality: Criticality) => void;
  onDismiss: () => void;
}) {
  const [criticality, setCriticality] = useState<Criticality>('MEDIUM');
  const status = STATUS[p.status];
  const decidable = p.status !== 'COVERED';
  const unclaimed = new Set(p.unclaimedHosts);

  return (
    <li className={`proposal${selected ? ' is-selected' : ''}`}>
      <div className="proposal-head">
        {mayWrite && decidable ? (
          <input
            type="checkbox"
            aria-label={`Select ${p.name}`}
            checked={selected}
            onChange={onToggle}
          />
        ) : null}
        <div className="proposal-title">
          <strong>{p.name}</strong> <span className={`status ${status.cls}`}>{status.label}</span>
          <span className="muted proposal-confidence">
            {' '}
            · confidence {p.confidence}/10 · named from{' '}
            {p.nameSource === 'TAG' ? 'a tag' : p.nameSource === 'STACK' ? 'its stack' : 'its host'}
          </span>
        </div>
      </div>

      <ul className="proposal-hosts mono">
        {p.hosts.map((h) => (
          <li key={h}>
            {h}
            {unclaimed.has(h) ? <span className="change-new">no owner</span> : null}
          </li>
        ))}
      </ul>
      {p.awsNamesOnly ? (
        <p className="field-hint">
          Only an AWS-assigned name reaches this — no DNS name points at it. Often an origin or a
          forgotten resource.
        </p>
      ) : null}

      {p.coveredBy.length > 0 ? (
        <p className="muted proposal-owned">
          Owned in part by{' '}
          {p.coveredBy.map((a, i) => (
            <span key={a.applicationId}>
              {i > 0 ? ', ' : ''}
              <Link className="table-link" to={`/app/surface?app=${a.applicationId}`}>
                {a.name}
              </Link>
            </span>
          ))}
          .
        </p>
      ) : null}

      <details className="policy-details">
        <summary>
          Why, and what it is made of — {p.components.length} components, {p.resources.length}{' '}
          resources
        </summary>
        <h4 className="detail-title">Evidence</h4>
        <ul className="proposal-evidence">
          {p.evidence.map((e) => (
            <li key={`${e.kind}${e.detail}`}>
              <span className="pill">{EVIDENCE[e.kind]}</span>{' '}
              <span className="mono">{e.detail}</span>
            </li>
          ))}
        </ul>
        <h4 className="detail-title">Components it would start with</h4>
        <ul className="component-chips">
          {p.components.map((c) => (
            <li key={c.ref} className="pill" title={COMPONENT_KIND_LABEL[c.kind]}>
              {c.name}
            </li>
          ))}
        </ul>
        <h4 className="detail-title">Draft scope</h4>
        <p className="field-hint">
          Offered to the first scope of an engagement for this application — never a scope on its
          own. A person still sets the window and authorisation, and an approver signs it off.
        </p>
        <pre className="policy-json">
          {p.draftScope.allowedHosts.join('\n')}
          {'\n\n'}
          {p.draftScope.seedUrls.join('\n')}
        </pre>
      </details>

      {mayWrite && decidable ? (
        <div className="row-actions">
          <select
            className="input input-sm"
            aria-label={`Criticality for ${p.name}`}
            value={criticality}
            onChange={(e) => setCriticality(e.target.value as Criticality)}
          >
            {(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy}
            onClick={() => onAccept(criticality)}
          >
            Accept as application
          </button>
          {p.status !== 'DISMISSED' ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={onDismiss}
            >
              Not an application
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
